import React, { FC } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import OnboardingShell from "./OnboardingShell";
import { Dictionary } from "./i18n/strings";
import { useLocale } from "./i18n/locale";

export interface TenantSignContractPageProps {
  className?: string;
  /** 4 for Flow A (short onboarding), 7 for Flow B (full onboarding). */
  totalSteps?: number;
}

interface OnboardingDocument {
  id: string;
  title: string;
  caption: string;
}

// TODO: the document set comes from the DMS — the team member picks the main
// contract and ticks the stay attestation per reservation. Placeholder until
// that contract exists; the attestation is optional and may be absent.
const getDocuments = (t: Dictionary): OnboardingDocument[] => [
  {
    id: "main-contract",
    title: t.contract.mainContractTitle,
    caption: t.contract.mainContractCaption,
  },
  {
    id: "stay-attestation",
    title: t.contract.attestationTitle,
    caption: t.contract.attestationCaption,
  },
];

/**
 * A filled page with a folded corner and ruled text, as drawn in the mockup —
 * not a plain outline.
 */
const DocumentIcon = () => (
  <svg
    className="h-14 w-14 flex-none"
    viewBox="0 0 28 34"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M3 3.5A2.5 2.5 0 015.5 1h11L26 10.5v20A2.5 2.5 0 0123.5 33h-18A2.5 2.5 0 013 30.5z"
      fill="#F1F1F3"
      stroke="#DCDCE0"
      strokeWidth={1.1}
    />
    <path
      d="M16.5 1v9.5H26"
      fill="#FFFFFF"
      stroke="#DCDCE0"
      strokeWidth={1.1}
      strokeLinejoin="round"
    />
    <g stroke="#A8ADB8" strokeWidth={1.5} strokeLinecap="round">
      <path d="M8 14h8" />
      <path d="M8 18h12M8 21.5h12M8 25h12" />
      <path d="M8 28.5h10" />
    </g>
  </svg>
);

const TenantSignContractPage: FC<TenantSignContractPageProps> = ({
  className = "",
  totalSteps = 7,
}) => {
  const navigate = useNavigate();
  const { t } = useLocale();
  const documents = getDocuments(t);

  const handleSign = () => {
    // TODO: hand off to the e-signature provider before advancing. One
    // signature covers every document listed above.
    navigate("/tenant/ready-for-arrival");
  };

  return (
    <div
      className={`nc-TenantSignContractPage ${className}`}
      data-nc-id="TenantSignContractPage"
    >
      <Helmet>
        <title>{t.contract.documentTitle}</title>
      </Helmet>

      <OnboardingShell
        step={3}
        totalSteps={totalSteps}
        title={t.contract.title}
        subtitle={t.contract.subtitle}
        footnote={
          <>
            <span>{t.contract.poweredBy}</span>
            {/* TODO: no Yousign logo asset is bundled yet. */}
            <span className="font-semibold text-neutral-500">Yousign</span>
          </>
        }
      >
        <ul className="flex flex-col gap-3">
          {documents.map((doc) => (
            <li
              key={doc.id}
              className="flex items-center gap-3 rounded-md border border-neutral-200/80 bg-white p-4"
            >
              <DocumentIcon />

              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-semibold leading-snug text-navy-900">
                  {doc.title}
                </p>
                <p className="mt-0.5 text-xs text-neutral-500">{doc.caption}</p>

                {/* TODO: open the document in the PDF viewer (EN/FR per the
                    language the team member selected before sending). */}
                <button
                  type="button"
                  className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-blue-600 transition-colors hover:text-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                >
                  {t.contract.viewDocument}
                  <svg
                    className="h-3.5 w-3.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.6}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>

              <span className="flex-none rounded-md border border-green-600 bg-white px-2.5 text-[10px] font-semibold uppercase tracking-wide text-green-700">
                {t.contract.toSign}
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-4 flex items-center gap-3 rounded-md border border-primary-6000/20 bg-cream-100 px-4 py-3">
          <span className="flex h-7 w-7 flex-none items-center justify-center text-primary-6000">
            <svg
              className="h-8 w-8"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.7}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
            <path d="M12 21.5c4.6-2 6.8-5.2 6.8-9.4V5.9L12 3.2 5.2 5.9v6.2c0 4.2 2.2 7.4 6.8 9.4z" />
            <path d="M9.2 12.2l2 2 3.6-3.8" />
            </svg>
          </span>
          <p className="text-[13px] leading-relaxed text-neutral-600 font-semibold">
            {t.contract.note}
          </p>
        </div>

        <button
          type="button"
          onClick={handleSign}
          className="mt-4 flex w-full items-center justify-between gap-3 rounded-md bg-navy-800 px-6 py-4 text-sm font-semibold tracking-wide text-white transition-colors hover:bg-navy-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-800 focus-visible:ring-offset-2"
        >
          <span className="flex-1 text-center">{t.contract.cta}</span>
          <svg
            className="h-5 w-5 flex-none"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 12h18m0 0l-7-7m7 7l-7 7" />
          </svg>
        </button>
      </OnboardingShell>
    </div>
  );
};

export default TenantSignContractPage;
